/**
 * Terms of Service — placeholder / draft legal notice.
 *
 * Mounted at /terms and (via alias route) /legal/terms. Publicly
 * accessible (no auth gate). V2 tokens, light-first, dark-opt-in.
 *
 * NOTE: Body copy is plain-English stub language pending counsel review.
 * The banner at the top makes this state visible to any reader.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";

const LAST_UPDATED = new Date().toLocaleDateString("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

function Section({ id, title, children }) {
  return (
    <section
      id={id}
      data-testid={`legal-section-${id}`}
      className="mt-10 first:mt-0"
    >
      <h2
        className="text-v2-display-sm text-v2-text mb-3"
        style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
      >
        {title}
      </h2>
      <div className="space-y-3 text-v2-body-md text-v2-textMuted leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function TermsOfService() {
  return (
    <main
      data-testid="terms-of-service-page"
      className="min-h-screen bg-v2-background text-v2-text"
    >
      <div className="mx-auto max-w-3xl px-6 md:px-10 py-16 md:py-24">
        <Link
          to="/"
          data-testid="terms-of-service-back-home"
          className="inline-flex items-center gap-2 text-v2-caption uppercase tracking-[0.16em] text-v2-textMuted hover:text-v2-accent transition-colors mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
          Back to home
        </Link>

        <header className="mb-10">
          <p className="text-v2-caption uppercase tracking-[0.18em] text-v2-textMuted">
            Legal
          </p>
          <h1
            className="mt-2 text-v2-display-lg text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            Terms of Service
          </h1>
          <p
            data-testid="terms-of-service-last-updated"
            className="mt-3 text-v2-body-sm text-v2-textSubtle"
          >
            Last updated: {LAST_UPDATED}
          </p>
        </header>

        {/* Draft banner — meta-copy about page state. */}
        <div
          data-testid="terms-of-service-draft-banner"
          className="mb-12 rounded-v2-lg p-5 flex items-start gap-3"
          style={{
            background: "var(--v2-warning-soft)",
            border: "1px solid var(--v2-warning)",
          }}
        >
          <AlertTriangle
            className="w-5 h-5 mt-0.5 shrink-0"
            style={{ color: "var(--v2-warning)" }}
            aria-hidden="true"
          />
          <div>
            <div
              className="text-v2-body-md font-semibold"
              style={{ color: "var(--v2-warning)" }}
            >
              This is a temporary legal notice.
            </div>
            <p className="mt-1 text-v2-body-sm text-v2-textMuted">
              Formal Terms of Service and Privacy Policy are being prepared
              with legal counsel. For questions in the meantime, contact{" "}
              <a
                href="mailto:support@informedaccelerator.com"
                className="text-v2-accent hover:text-v2-accentStrong underline underline-offset-2"
              >
                support@informedaccelerator.com
              </a>
              .
            </p>
          </div>
        </div>

        <Section id="acceptance" title="1. Acceptance of Terms">
          <p>
            By creating an account or otherwise using the Informed OS
            OS platform ("Service"), you agree to these Terms of Service.
            These terms form a legally binding agreement between you and
            Informed Accelerator, Inc.
          </p>
          <p>
            If you do not agree with any portion of these terms, do not
            register for or use the Service.
          </p>
        </Section>

        <Section id="description" title="2. Description of Service">
          <p>
            Informed OS is an educational funding readiness and
            credit improvement platform. It computes a readiness score based
            on information you provide, generates draft dispute documents you
            may choose to review and mail, and surfaces matches to
            third-party funding partners you may already qualify for.
          </p>
        </Section>

        <Section id="accounts" title="3. User Accounts">
          <p>
            You are responsible for maintaining the confidentiality of your
            account credentials and for all activity that occurs under your
            account. Notify us promptly at{" "}
            <a
              href="mailto:support@informedaccelerator.com"
              className="text-v2-accent hover:text-v2-accentStrong underline underline-offset-2"
            >
              support@informedaccelerator.com
            </a>{" "}
            of any unauthorized use.
          </p>
          <p>
            You represent that all information you provide is accurate and
            that you are legally permitted to upload any documents (including
            credit reports) you submit to the Service.
          </p>
        </Section>

        <Section id="prohibited" title="4. Prohibited Uses">
          <p>
            You agree not to use the Service to upload documents you do not
            own or are not authorized to submit, to attempt to reverse
            engineer or disrupt the platform, or to violate any applicable
            law. We may suspend or terminate accounts we reasonably believe
            are engaging in prohibited conduct.
          </p>
        </Section>

        <Section id="educational" title="5. Educational Nature of the Service">
          <p>
            <strong className="text-v2-text">
              Informed OS does not provide legal, financial, or
              credit-repair guarantees.
            </strong>{" "}
            It provides educational analysis and draft documents based on
            information you provide. Approval, funding, score increases, and
            account removals are not guaranteed. Please review all information
            before submitting documents or applying for funding.
          </p>
          <p>
            Draft dispute letters generated by the Service are provided for
            your review and — at your sole discretion — for you to send. We
            do not mail letters on your behalf, and we do not represent you
            before any credit bureau, furnisher, court, or lender.
          </p>
        </Section>

        <Section id="termination" title="6. Termination">
          <p>
            You may terminate your account at any time from your account
            settings, or by contacting support. We may terminate or suspend
            your access if we reasonably determine you have breached these
            terms or if we discontinue the Service.
          </p>
        </Section>

        <Section id="liability" title="7. Limitation of Liability">
          <p>
            To the maximum extent permitted by applicable law, Informed
            Accelerator, Inc. and its affiliates are not liable for any
            indirect, incidental, special, or consequential damages arising
            out of or related to your use of the Service. Nothing in these
            terms limits liability where such limitation is prohibited by law.
          </p>
        </Section>

        <Section id="governing-law" title="8. Governing Law">
          <p>
            These terms are governed by the laws of the jurisdiction in which
            Informed Accelerator, Inc. is organized, without regard to its
            conflict-of-laws principles. Formal choice-of-law and dispute
            resolution provisions will be published in the counsel-reviewed
            version of this document.
          </p>
        </Section>

        <Section id="contact" title="9. Contact">
          <p>
            Questions about these Terms? Reach us at{" "}
            <a
              href="mailto:support@informedaccelerator.com"
              className="text-v2-accent hover:text-v2-accentStrong underline underline-offset-2"
            >
              support@informedaccelerator.com
            </a>
            . We aim to respond within five business days.
          </p>
        </Section>
      </div>
    </main>
  );
}
